import React, {createContext, useContext, useEffect, useReducer, useRef} from 'react';
import {useMutation, useQuery} from 'react-query';
import {useDispatch} from "react-redux";

import {initialState} from './actions';
import {reducer} from './HospitalEvents.reducer';
import {addHospitalEvent, editHospitalEvent, getHospitalEvents} from './api';
import {getGetters} from './getters';
import {getToggles} from './toggles';
import {getSetters} from "./setters";

import {useEQHToastContext} from '../../../../components/toast/EQHToast.context';

const HospitalEventsContext = createContext();

const HospitalEventsProvider = ({ children }) => {
  const globalDispatch = useDispatch();
  const {removeNotification, setNotification} = useEQHToastContext();
  const [state, dispatch] = useReducer(reducer, initialState);
  const lastFetchedRequestKeyRef = useRef(null);
  const setters = getSetters(dispatch);
  const toggles = getToggles(dispatch, globalDispatch);

  const { refetch: getHospitalEventsQuery } = useQuery({
    queryKey: ['getHospitalEvents', state?.hospitalEventsRequest?.patientId, state?.hospitalEventsRequest?.requestBody],
    queryFn: () => {
      return getHospitalEvents(
        state?.hospitalEventsRequest?.patientId,
        state?.hospitalEventsRequest?.requestBody
      )
        .then((res) => {
          setters.setHospitalEventsList(res.data.patientAdtDtos);
          setters.setTotalSize(res.data.totalSize);
        })
    },
    refetchOnWindowFocus: false,
    enabled: false
  })

  const { mutate: mutateAddHospitalEvent } = useMutation(addHospitalEvent, {
    onSuccess: (res, variables) => {
      if (res.data === "ADT_DUPLICATE") {
        setNotification("Error", "Hospital Event already available for the patient with the provided admit and discharge dates and disposition field.");
      } else {
        const patientId = variables?.patientId;
        const requestBody = state?.hospitalEventsRequest?.requestBody;
        setters.setHospitalEventsList([]);
        setters.setHospitalEventsRequest({ patientId, requestBody });
        toggles.toggleNewHospitalEventModalActive(false);
      }
    },
    onError: (err) => {
      toggles.toggleNewHospitalEventModalActive(false);
    },
  });

  const { mutate: mutateEditHospitalEvent } = useMutation(editHospitalEvent, {
    onSuccess: (res, variables) => {
      const patientId = variables?.patientId;
      const requestBody = state?.hospitalEventsRequest?.requestBody;
      setters.setHospitalEventsList([]);
      setters.setHospitalEventsRequest({ patientId, requestBody });
      toggles.toggleEditHospitalEventModalActive(false);
    },
    onError: (err) => {
      toggles.toggleEditHospitalEventModalActive(false);
    },
  });
  const hospitalEventsRequest = state?.hospitalEventsRequest;
  const addHospitalEventRequest = state?.addHospitalEventRequest;
  const editHospitalEventRequest = state?.editHospitalEventRequest;

  useEffect(() => {
    if(!hospitalEventsRequest?.patientId) {
      return;
    }

    const requestKey = JSON.stringify(hospitalEventsRequest);
    if (lastFetchedRequestKeyRef.current === requestKey) {
      return;
    }

    lastFetchedRequestKeyRef.current = requestKey;
    getHospitalEventsQuery()
  }, [getHospitalEventsQuery, hospitalEventsRequest])

  useEffect(() => {
    if(addHospitalEventRequest?.patientId) {
      setters.setAddHospitalEventRequest({});
      mutateAddHospitalEvent(addHospitalEventRequest);
    }
  }, [addHospitalEventRequest, mutateAddHospitalEvent, setters])

  useEffect(() => {
    if(editHospitalEventRequest?.patientId) {
      setters.setEditHospitalEventRequest({});
      mutateEditHospitalEvent(editHospitalEventRequest);
    }
  }, [editHospitalEventRequest, mutateEditHospitalEvent, setters])

  const getters = getGetters(state);

  const callbacks = {
    getHospitalEvents: async (patientId, requestBody) => {
      removeNotification();
      setters.setHospitalEventsList([])
      setters.setHospitalEventsRequest({ patientId, requestBody });
    },
    addHospitalEvent: async (requestBody) => {
      removeNotification();
      setters.setAddHospitalEventRequest(requestBody);
    },
    editHospitalEvent: async (requestBody) => {
      removeNotification();
      setters.setEditHospitalEventRequest(requestBody);
    }
  }

  const value = {
    ...setters,
    ...getters,
    ...toggles,
    ...callbacks
  }

  return (
    <HospitalEventsContext.Provider value={value}>
      {children}
    </HospitalEventsContext.Provider>
  )
};
const useHospitalEventsContext = () => useContext(HospitalEventsContext);

export {
  HospitalEventsProvider,
  useHospitalEventsContext,
}
